import { useState, useEffect, useRef } from 'react';
import { isColorDark, hexToRgba, resolveLinkProps, resolveImage } from '../utils';

export default function ExitIntentSection({ block = {}, primaryColor, secondaryColor, mediaFolders = {}, isPreview }) {
    const { config = {}, content = {} } = block;
    const [visible, setVisible] = useState(!!isPreview);
    const [dismissed, setDismissed] = useState(false);
    const [animateIn, setAnimateIn] = useState(!!isPreview);

    const variant = config.variant || 'modal';
    const showOnce = config.show_once !== false;
    const delay = (config.delay || 0) * 1000;
    const accent = config.accent_color || primaryColor;
    const storageKey = `exit_intent_${block.id || 'default'}_seen`;

    const imageUrl = resolveImage(content, 'image', mediaFolders) || content.image_url;
    const dialogRef = useRef(null);

    useEffect(() => {
        if (isPreview) return;
        if (typeof sessionStorage !== 'undefined' && showOnce && sessionStorage.getItem(storageKey)) {
            setDismissed(true);
            return;
        }

        let delayTimer = null;
        let shown = false;

        const show = () => {
            if (shown) return;
            shown = true;
            setVisible(true);
            setTimeout(() => setAnimateIn(true), 30);
            if (typeof sessionStorage !== 'undefined' && showOnce) {
                sessionStorage.setItem(storageKey, '1');
            }
        };

        // Exit intent: mouse leaves viewport top
        const handleMouseOut = (e) => {
            if (e.clientY <= 0 && !shown) {
                show();
            }
        };

        document.addEventListener('mouseout', handleMouseOut);

        // Delay fallback
        if (delay > 0) {
            delayTimer = setTimeout(() => {
                if (!shown) show();
            }, delay);
        }

        return () => {
            document.removeEventListener('mouseout', handleMouseOut);
            if (delayTimer) clearTimeout(delayTimer);
        };
    }, []);

    // Focus the dialog when it becomes visible & handle Escape key
    useEffect(() => {
        if (!visible || dismissed) return;
        dialogRef.current?.focus();
        const handleKeyDown = (e) => { if (e.key === 'Escape') { setAnimateIn(false); setTimeout(() => setDismissed(true), 300); } };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [visible, dismissed]);

    if ((!visible && !isPreview) || dismissed || !content.title) return null;

    const close = () => {
        setAnimateIn(false);
        setTimeout(() => setDismissed(true), 300);
    };

    const ctaProps = content.cta_link ? resolveLinkProps(content.cta_link) : null;

    const CloseButton = ({ className = '', light = false }) => (
        <button
            onClick={close}
            aria-label="Cerrar"
            className={`p-1.5 rounded-full transition-colors ${light ? 'hover:bg-white/20 text-white' : 'hover:bg-gray-100 text-gray-400'} ${className}`}
        >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>
    );

    const OfferBadge = () => content.offer_badge ? (
        <div className="absolute -top-2 -right-2 z-20">
            <span className="inline-block px-3 py-1.5 rounded-full text-xs font-extrabold text-white shadow-lg transform rotate-3"
                style={{ backgroundColor: secondaryColor || '#ef4444' }}>
                {content.offer_badge}
            </span>
        </div>
    ) : null;

    const ContentBlock = () => (
        <>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{content.title}</h3>
            {content.subtitle && <p className="text-sm font-medium mb-1" style={{ color: accent }}>{content.subtitle}</p>}
            {content.description && <p className="text-sm text-gray-500 mb-5 leading-relaxed">{content.description}</p>}
            {content.cta_text && ctaProps && (
                <a {...ctaProps}
                    onClick={(e) => { ctaProps.onClick?.(e); close(); }}
                    className="block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] hover:shadow-lg text-white shadow-md"
                    style={{ backgroundColor: accent }}
                >
                    {content.cta_text}
                </a>
            )}
            {content.dismiss_label && (
                <button onClick={close} className="block w-full text-center mt-3 text-sm transition-colors hover:opacity-70" style={{ color: '#9ca3af' }}>
                    {content.dismiss_label}
                </button>
            )}
        </>
    );

    // ── Modal variant (default) ──
    if (variant === 'modal') {
        return (
            <div
                className={`${isPreview ? 'relative w-full' : `fixed inset-0 z-[9999] transition-all duration-300 ${animateIn ? 'bg-black/50' : 'bg-black/0'}`} flex items-center justify-center p-4`}
                onClick={close}
            >
                <div
                    ref={dialogRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={content.title}
                    tabIndex={-1}
                    className={`relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden transition-all duration-300 ${animateIn ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}`}
                    onClick={e => e.stopPropagation()}
                >
                    <OfferBadge />
                    <CloseButton className="absolute top-3 right-3 z-10" />

                    {imageUrl ? (
                        <div className="flex flex-col md:flex-row">
                            <div className="md:w-2/5 shrink-0">
                                <img src={imageUrl} alt="" className="w-full h-48 md:h-full object-cover" loading="lazy" decoding="async" />
                            </div>
                            <div className="p-6 md:p-8 flex-1 flex flex-col justify-center relative">
                                <ContentBlock />
                            </div>
                        </div>
                    ) : (
                        <div className="p-6 md:p-8 text-center relative">
                            <ContentBlock />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Slide-bottom variant ──
    if (variant === 'slide-bottom') {
        return (
            <div className={`${isPreview ? 'relative w-full' : 'fixed inset-x-0 bottom-0 z-[9999] pointer-events-none'} flex justify-center p-4`}>
                <div
                    ref={dialogRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={content.title}
                    tabIndex={-1}
                    className={`relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden pointer-events-auto transition-all duration-500 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'}`}
                >
                    <OfferBadge />
                    <CloseButton className="absolute top-3 right-3 z-10" />

                    <div className="flex flex-col sm:flex-row">
                        {imageUrl && (
                            <div className="sm:w-2/5 shrink-0">
                                <img src={imageUrl} alt="" className="w-full h-40 sm:h-full object-cover" loading="lazy" decoding="async" />
                            </div>
                        )}
                        <div className={`p-5 flex-1 relative ${!imageUrl ? 'text-center' : ''}`}>
                            <ContentBlock />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Side-drawer variant ──
    return (
        <>
            {/* Overlay */}
            <div
                className={`${isPreview ? 'hidden' : `fixed inset-0 z-[9998] transition-all duration-300 ${animateIn ? 'bg-black/40' : 'bg-black/0 pointer-events-none'}`}`}
                onClick={close}
            />
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-label={content.title}
                tabIndex={-1}
                className={`${isPreview ? 'relative w-full max-w-sm mx-auto' : `fixed top-0 right-0 bottom-0 z-[9999] w-full max-w-sm transition-all duration-500 ${animateIn ? 'translate-x-0' : 'translate-x-full'}`} bg-white shadow-2xl overflow-y-auto`}
            >
                <CloseButton className="absolute top-4 right-4 z-10" />

                {imageUrl && (
                    <div className="relative">
                        <img src={imageUrl} alt="" className="w-full h-56 object-cover" loading="lazy" decoding="async" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                        {content.offer_badge && (
                            <div className="absolute top-4 left-4">
                                <span className="inline-block px-3 py-1.5 rounded-full text-xs font-extrabold text-white shadow-lg"
                                    style={{ backgroundColor: secondaryColor || '#ef4444' }}>
                                    {content.offer_badge}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                <div className="p-6 relative">
                    {!imageUrl && <OfferBadge />}
                    <ContentBlock />
                </div>
            </div>
        </>
    );
}
